/**
 * The two halves of a CSV download, written once.
 *
 * Four views build a CSV by hand and each one had spelled out the same quoting rule and the same
 * anchor-click dance at its own callsite. The quoting is the part worth centralising: a site name
 * with a comma in it, or a note with a newline, silently shifts every later column of that row,
 * and a copy of the rule that drifts produces a corrupt file rather than an ugly one.
 */

/** Quote a cell only where the delimiter, a quote, or a line break would otherwise break the row. */
export function escapeCsvValue(value: unknown): string {
  if (value === null || value === undefined) return ""
  const str = String(value)
  if (str.includes(",") || str.includes('"') || str.includes("\n") || str.includes("\r")) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

/** Join headers and rows into CSV text, escaping every cell. */
export function buildCsv(headers: string[], rows: unknown[][]): string {
  return [
    headers.map(escapeCsvValue).join(","),
    ...rows.map((row) => row.map(escapeCsvValue).join(",")),
  ].join("\n")
}

/** Hand the browser a CSV to save under `filename`. */
export function downloadCsv(filename: string, csvContent: string): void {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.setAttribute("href", url)
  link.setAttribute("download", filename)
  link.style.visibility = "hidden"
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
